import { environment } from '@environment';

const CONFIG_ENDPOINT = '/api/config';

/**
 * Shape returned by `/api/config`. Mirrors the compile-time `environment.ts`
 * so the runtime payload can simply hydrate it. Keep this in sync when adding
 * keys to the environment.
 */
export interface RuntimeConfig {
  apim?: Partial<typeof environment.apim>;
  paths?: Partial<typeof environment.paths>;
  msal?: Partial<typeof environment.msal>;
}

/**
 * Hosts where the compile-time environment is authoritative and no runtime
 * fetch should occur (so `ng serve` / `node server.js` against a local build
 * keep working with the values baked in `environment.ts`).
 */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

export function isLocalHost(): boolean {
  if (typeof location === 'undefined') return false;
  return LOCAL_HOSTS.has(location.hostname);
}

/**
 * Resolves the runtime configuration before Angular bootstraps.
 *
 * Any deployed build (anything not running on a local host) fetches
 * `/api/config` and mutates the imported `environment` with the response, so
 * the same bundle can be deployed to multiple Azure Web Apps and pick up the
 * App Settings of whichever environment it runs in.
 *
 * On local hosts the fetch is skipped and the compile-time `environment.ts`
 * values (localhost API URLs, etc.) are used as-is.
 *
 * Rejects when the fetch fails or returns a non-OK response so the caller can
 * abort the bootstrap and render an error screen instead of silently running
 * with empty config.
 */
export async function loadRuntimeConfig(
  fetchImpl: typeof fetch = fetch,
  isLocal: () => boolean = isLocalHost
): Promise<void> {
  if (isLocal()) return;

  let response: Response;
  try {
    response = await fetchImpl(CONFIG_ENDPOINT, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
  } catch (cause) {
    throw new Error(`No se pudo contactar ${CONFIG_ENDPOINT}: ${describe(cause)}`);
  }

  if (!response.ok) {
    throw new Error(`${CONFIG_ENDPOINT} respondió ${response.status} ${response.statusText}`);
  }

  let payload: RuntimeConfig;
  try {
    payload = (await response.json()) as RuntimeConfig;
  } catch (cause) {
    throw new Error(`${CONFIG_ENDPOINT} devolvió un JSON inválido: ${describe(cause)}`);
  }

  applyConfig(payload);
}

function applyConfig(payload: RuntimeConfig): void {
  if (payload.apim) {
    Object.assign(environment.apim, payload.apim);
  }
  if (payload.paths) {
    Object.assign(environment.paths, payload.paths);
  }
  if (payload.msal) {
    Object.assign(environment.msal, payload.msal);
  }
}

function describe(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  if (typeof cause === 'string') return cause;
  return 'error desconocido';
}
